import { useMemo, useState, useCallback } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { cn } from '@/lib/cn';
import { colors } from '@/design/tokens';
import { formatNumber, formatDate } from '@/lib/format';
import { evaluateFormula } from '@/lib/formula';

interface FormulaField {
  id: string;
  name: string;
  formula: string;
  events: string[];
}

interface TimeSeriesChartProps {
  data: Record<string, unknown> | undefined;
  className?: string;
  /** Active custom formula field definitions (multiple) */
  formulaFields?: FormulaField[];
  /** Supplementary data per-field (keyed by field ID) with per-event filters */
  formulaDataMap?: Record<string, Record<string, unknown>>;
  /** Events explicitly selected by the user */
  selectedEvents?: string[];
  /** Comparison period data (same shape, different date range — aligned by index) */
  comparisonData?: Record<string, unknown>;
  /** Apply cumulative sums client-side */
  cumulative?: boolean;
}

interface ApiDataPoint {
  date: string;
  value: number;
  breakdown_value?: string;
  event_name?: string;
}

interface ChartDataPoint {
  date: string;
  [key: string]: string | number;
}

/**
 * Time series line chart using Recharts.
 * Supports multiple series (breakdown by property).
 *
 * API returns: { timeSeries: [{ date, value, breakdown_value? }, ...] }
 * We pivot breakdown_value into separate series keys for Recharts.
 */
export function TimeSeriesChart({ data, className, formulaFields, formulaDataMap, selectedEvents, comparisonData, cumulative }: TimeSeriesChartProps) {
  const { chartData, seriesKeys } = useMemo(() => {
    if (!data) return { chartData: [] as ChartDataPoint[], seriesKeys: [] as string[] };

    // Accept { timeSeries: [...] } or { rows: [...] }
    const raw: ApiDataPoint[] =
      (data as { timeSeries?: ApiDataPoint[] }).timeSeries ||
      (data as { rows?: ApiDataPoint[] }).rows ||
      [];
    if (raw.length === 0) return { chartData: [] as ChartDataPoint[], seriesKeys: [] as string[] };

    // Check if there's a breakdown
    const hasBreakdown = raw.some((r) => r.breakdown_value !== undefined && r.breakdown_value !== '');

    // Formula-only events: in formulas but not user-selected
    const formulaOnlyEvents = new Set<string>();
    if (formulaFields?.length) {
      for (const f of formulaFields) {
        for (const e of f.events) {
          if (!selectedEvents?.includes(e)) formulaOnlyEvents.add(e);
        }
      }
    }

    if (!hasBreakdown) {
      // Simple: one series called "count"
      const chartData: ChartDataPoint[] = raw.map((r) => ({
        date: r.date,
        count: r.value,
      }));

      let seriesKeys: string[] = ['count'];

      // Evaluate formula fields even without breakdown.
      // In no-breakdown mode there's at most 1 merged event (2+ would
      // trigger multi-event mode which auto-breaks down by event_name).
      // We map the "count" value to that single event name so the
      // formula parser can resolve the variable.
      if (formulaFields?.length) {
        const allFormulaEvents = new Set<string>();
        for (const f of formulaFields) {
          for (const e of f.events) allFormulaEvents.add(e);
        }
        // Determine the single event name (null when ambiguous / 0 events)
        const singleEvent = allFormulaEvents.size === 1
          ? [...allFormulaEvents][0]
          : null;

        // Build per-field supplementary date→value maps (for per-event filters)
        const fieldDateValues = new Map<string, Map<string, number>>();
        if (formulaDataMap) {
          for (const field of formulaFields) {
            const fieldData = formulaDataMap[field.id];
            if (!fieldData) continue;
            const fRaw: ApiDataPoint[] =
              (fieldData as { timeSeries?: ApiDataPoint[] }).timeSeries ||
              (fieldData as { rows?: ApiDataPoint[] }).rows ||
              [];
            const dateMap = new Map<string, number>();
            for (const r of fRaw) {
              dateMap.set(r.date, r.value);
            }
            fieldDateValues.set(field.id, dateMap);
          }
        }

        for (const field of formulaFields) {
          const fieldDates = fieldDateValues.get(field.id);

          for (const point of chartData) {
            const date = point.date as string;
            const eventValues: Record<string, number> = {};

            if (singleEvent) {
              // Prefer filtered supplementary data when available
              if (fieldDates?.has(date)) {
                eventValues[singleEvent] = fieldDates.get(date)!;
              } else {
                eventValues[singleEvent] = point.count as number;
              }
            }

            try {
              point[field.name] = evaluateFormula(field.formula, eventValues);
            } catch {
              point[field.name] = 0;
            }
          }
          seriesKeys.push(field.name);
        }

        // Hide the raw event series if it's only there for formulas
        if (singleEvent && formulaOnlyEvents.has(singleEvent)) {
          seriesKeys = seriesKeys.filter((k) => k !== 'count');
        }
      }

      return { chartData, seriesKeys };
    }

    // Check if backend returned per-event breakdown (multiEvent + breakdown)
    const hasEventName = raw.some((r) => r.event_name !== undefined && r.event_name !== '');

    if (hasEventName && formulaFields?.length) {
      // ── Multi-event + breakdown + formula mode ──────────────────────────
      // Data shape: { date, breakdown_value, event_name, value }
      // We need to:
      //   1. Build a nested map: date → breakdown_value → { event_name: count }
      //   2. Evaluate formula for each (date, breakdown_value)
      //   3. Show formula result as series per breakdown_value

      // Collect all unique breakdown values and event names
      const breakdownSet = new Set<string>();
      // nested: date → breakdown → event → value
      const nested = new Map<string, Map<string, Record<string, number>>>();

      for (const row of raw) {
        const bv = row.breakdown_value || 'other';
        const ev = row.event_name!;
        breakdownSet.add(bv);
        if (!nested.has(row.date)) nested.set(row.date, new Map());
        const bdMap = nested.get(row.date)!;
        if (!bdMap.has(bv)) bdMap.set(bv, {});
        bdMap.get(bv)![ev] = (bdMap.get(bv)![ev] || 0) + row.value;
      }

      const dateMap = new Map<string, ChartDataPoint>();
      const seriesSet = new Set<string>();

      // For each formula field, compute per-breakdown series
      for (const field of formulaFields) {
        for (const [date, bdMap] of nested) {
          if (!dateMap.has(date)) dateMap.set(date, { date });
          const point = dateMap.get(date)!;

          for (const [bv, eventCounts] of bdMap) {
            // Series name: "formula_name (breakdown_value)" or just "breakdown_value" when 1 formula
            const seriesName = formulaFields.length > 1
              ? `${field.name} (${bv})`
              : bv;
            seriesSet.add(seriesName);
            try {
              point[seriesName] = evaluateFormula(field.formula, eventCounts);
            } catch {
              point[seriesName] = 0;
            }
          }
        }
      }

      // Also add raw event series per breakdown if any events are user-selected (not formula-only)
      if (selectedEvents?.length) {
        for (const [date, bdMap] of nested) {
          const point = dateMap.get(date)!;
          for (const [bv, eventCounts] of bdMap) {
            for (const ev of selectedEvents) {
              if (formulaOnlyEvents.has(ev)) continue;
              const seriesName = `${ev} (${bv})`;
              seriesSet.add(seriesName);
              point[seriesName] = eventCounts[ev] || 0;
            }
          }
        }
      }

      const chartData = Array.from(dateMap.values()).sort((a, b) =>
        String(a.date).localeCompare(String(b.date)),
      );
      return { chartData, seriesKeys: Array.from(seriesSet) };
    }

    // ── Standard pivot: group by date, each breakdown_value becomes a column ──
    const dateMap = new Map<string, ChartDataPoint>();
    const seriesSet = new Set<string>();

    for (const row of raw) {
      const seriesName = row.breakdown_value || 'other';
      seriesSet.add(seriesName);
      if (!dateMap.has(row.date)) {
        dateMap.set(row.date, { date: row.date });
      }
      const existing = (dateMap.get(row.date)![seriesName] as number) || 0;
      dateMap.get(row.date)![seriesName] = existing + row.value;
    }

    // Build supplementary formula data per-field per-date
    const formulaDateMaps = new Map<string, Map<string, Record<string, number>>>();
    if (formulaFields?.length && formulaDataMap) {
      for (const field of formulaFields) {
        const fieldData = formulaDataMap[field.id];
        if (!fieldData) continue;
        const fRaw: ApiDataPoint[] =
          (fieldData as { timeSeries?: ApiDataPoint[] }).timeSeries ||
          (fieldData as { rows?: ApiDataPoint[] }).rows ||
          [];
        const map = new Map<string, Record<string, number>>();
        for (const r of fRaw) {
          const series = r.breakdown_value || 'value';
          if (!map.has(r.date)) map.set(r.date, {});
          map.get(r.date)![series] = r.value;
        }
        formulaDateMaps.set(field.id, map);
      }
    }

    // Evaluate formula fields per date and add as new series
    if (formulaFields?.length) {
      const allSeries = Array.from(seriesSet);
      for (const field of formulaFields) {
        const formulaSeriesName = field.name;
        seriesSet.add(formulaSeriesName);

        const fieldDateMap = formulaDateMaps.get(field.id);

        for (const [date, point] of dateMap) {
          // Gather event values for this date
          const eventValues: Record<string, number> = {};
          if (fieldDateMap?.has(date)) {
            // Use filtered data from supplementary query
            Object.assign(eventValues, fieldDateMap.get(date));
          } else {
            // Use main data — breakdown values are event names (when breakdown = event_name)
            for (const series of allSeries) {
              const v = point[series];
              if (typeof v === 'number') eventValues[series] = v;
            }
          }
          try {
            point[formulaSeriesName] = evaluateFormula(field.formula, eventValues);
          } catch {
            point[formulaSeriesName] = 0;
          }
        }
      }

      // Remove formula-only events from visible series
      for (const ev of formulaOnlyEvents) seriesSet.delete(ev);
    }

    const chartData = Array.from(dateMap.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
    const seriesKeys = Array.from(seriesSet);
    return { chartData, seriesKeys };
  }, [data, formulaFields, formulaDataMap, selectedEvents]);

  // ─── Apply cumulative sums client-side ──────────────────────────────
  const cumulativeData = useMemo(() => {
    if (!cumulative || chartData.length === 0) return chartData;

    const running: Record<string, number> = {};
    return chartData.map((point) => {
      const newPoint = { ...point };
      for (const key of seriesKeys) {
        const val = Number(point[key] ?? 0);
        running[key] = (running[key] ?? 0) + val;
        newPoint[key] = running[key];
      }
      return newPoint;
    });
  }, [chartData, seriesKeys, cumulative]);

  // ─── Merge comparison data by index ─────────────────────────────────
  const { mergedData, cmpKeys } = useMemo(() => {
    if (!comparisonData || cumulativeData.length === 0) {
      return { mergedData: cumulativeData, cmpKeys: [] as string[] };
    }

    const cmpRaw: ApiDataPoint[] =
      (comparisonData as { timeSeries?: ApiDataPoint[] }).timeSeries ||
      (comparisonData as { rows?: ApiDataPoint[] }).rows ||
      [];
    if (cmpRaw.length === 0) return { mergedData: cumulativeData, cmpKeys: [] as string[] };

    const hasBreakdown = cmpRaw.some((r) => r.breakdown_value !== undefined && r.breakdown_value !== '');

    if (!hasBreakdown) {
      // Simple mode: one comparison series "count__cmp"
      const cmpSorted = [...cmpRaw].sort((a, b) => a.date.localeCompare(b.date));
      let cmpRunning = 0;
      const merged = cumulativeData.map((point, i) => {
        const cmpPoint = cmpSorted[i];
        let cmpVal = cmpPoint ? cmpPoint.value : undefined;
        if (cumulative && cmpVal != null) {
          cmpRunning += cmpVal;
          cmpVal = cmpRunning;
        }
        return { ...point, count__cmp: cmpVal };
      });
      return { mergedData: merged, cmpKeys: seriesKeys.map((k) => `${k}__cmp`) };
    }

    // Breakdown mode: pivot comparison data by date, then align by index
    const cmpDateMap = new Map<string, Record<string, number>>();
    for (const row of cmpRaw) {
      const seriesName = row.breakdown_value || 'other';
      const date = row.date;
      if (!cmpDateMap.has(date)) cmpDateMap.set(date, {});
      cmpDateMap.get(date)![seriesName] = row.value;
    }
    const cmpRows = Array.from(cmpDateMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([, vals]) => vals);

    const cmpRunning: Record<string, number> = {};
    const merged = cumulativeData.map((point, i) => {
      const cmpRow = cmpRows[i];
      const extra: Record<string, number | undefined> = {};
      for (const key of seriesKeys) {
        let val = cmpRow ? cmpRow[key] : undefined;
        if (cumulative && val != null) {
          cmpRunning[key] = (cmpRunning[key] ?? 0) + val;
          val = cmpRunning[key];
        }
        extra[`${key}__cmp`] = val;
      }
      return { ...point, ...extra };
    });

    return { mergedData: merged, cmpKeys: seriesKeys.map((k) => `${k}__cmp`) };
  }, [cumulativeData, comparisonData, seriesKeys, cumulative]);

  // ─── Interactive legend: track hidden series ──────────────────────────
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  // Reset hidden series when seriesKeys change (new query run)
  const seriesKeyStr = seriesKeys.join('|');
  const [prevKeyStr, setPrevKeyStr] = useState(seriesKeyStr);
  if (seriesKeyStr !== prevKeyStr) {
    setPrevKeyStr(seriesKeyStr);
    setHiddenSeries(new Set());
  }

  const toggleSeries = useCallback((key: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  if (chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-sm text-slate-400">
        No data to display
      </div>
    );
  }

  const hasCmp = cmpKeys.length > 0;

  return (
    <div className={cn('w-full', className)}>
      {/* Chart header */}
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-slate-900">
          Time Series{cumulative ? ' (Cumulative)' : ''}
        </h3>
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={360}>
        <LineChart data={hasCmp ? mergedData : chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: '#94A3B8' }}
            tickLine={false}
            axisLine={{ stroke: '#E2E8F0' }}
            tickFormatter={(v) => {
              try {
                const s = String(v);
                if (s.includes('T') || /\d{4}-\d{2}-\d{2} \d{2}/.test(s)) {
                  return formatDate(s, 'MMM d HH:mm');
                }
                return formatDate(s, 'MMM d');
              } catch {
                return String(v);
              }
            }}
          />
          <YAxis
            tick={{ fontSize: 11, fill: '#94A3B8' }}
            tickLine={false}
            axisLine={false}
            width={60}
            tickFormatter={(v) => formatNumber(v)}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: '12px',
              boxShadow: '0 4px 12px -4px rgb(0 0 0 / 0.08)',
              padding: '8px 12px',
              fontSize: '12px',
            }}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            formatter={((value: any, name: any) => {
              const nameStr = String(name);
              if (hiddenSeries.has(nameStr) || hiddenSeries.has(nameStr.replace(/__cmp$/, ''))) {
                return [null, null];
              }
              const isCmpLine = nameStr.endsWith('__cmp');
              const displayName = isCmpLine ? `${nameStr.replace(/__cmp$/, '')} (cmp)` : nameStr;
              return [formatNumber(Number(value ?? 0)), displayName];
            }) as any}
            labelFormatter={(label) => {
              try {
                const s = String(label);
                if (s.includes('T') || /\d{4}-\d{2}-\d{2} \d{2}/.test(s)) {
                  return formatDate(s, 'MMM d, yyyy HH:mm');
                }
                return formatDate(s, 'MMM d, yyyy');
              } catch {
                return String(label);
              }
            }}
          />
          {/* Main lines */}
          {seriesKeys.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={colors.chart[i % colors.chart.length]}
              strokeWidth={2}
              dot={false}
              activeDot={hiddenSeries.has(key) ? false : { r: 4, strokeWidth: 0 }}
              hide={hiddenSeries.has(key)}
            />
          ))}
          {/* Comparison lines — dashed, lower opacity, same color */}
          {hasCmp && seriesKeys.map((key, i) => (
            <Line
              key={`${key}__cmp`}
              type="monotone"
              dataKey={`${key}__cmp`}
              stroke={colors.chart[i % colors.chart.length]}
              strokeWidth={1.5}
              strokeDasharray="6 3"
              strokeOpacity={0.4}
              dot={false}
              activeDot={hiddenSeries.has(key) ? false : { r: 3, strokeWidth: 0, fillOpacity: 0.4 }}
              hide={hiddenSeries.has(key)}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>

      {/* Unified interactive legend */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-3 pt-3 border-t border-slate-100">
        {seriesKeys.map((key, i) => {
          const color = colors.chart[i % colors.chart.length];
          const isHidden = hiddenSeries.has(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => toggleSeries(key)}
              className="flex items-center gap-1.5 px-1 py-0.5 rounded transition-opacity hover:bg-slate-50"
              style={{ opacity: isHidden ? 0.35 : 1 }}
            >
              <div
                className="w-3 h-0.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: color }}
              />
              <span className="text-xs text-slate-600 whitespace-nowrap">{key}</span>
              {hasCmp && (
                <>
                  <div
                    className="w-3 h-0.5 rounded-full flex-shrink-0 ml-1"
                    style={{
                      backgroundColor: color,
                      opacity: 0.4,
                      backgroundImage: `repeating-linear-gradient(90deg, ${color} 0 3px, transparent 3px 5px)`,
                      backgroundSize: '100% 1px',
                    }}
                  />
                  <span className="text-xs text-slate-400 whitespace-nowrap">cmp</span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
